import localStorage from '@stubhub/local-storage';

import {getLocalStorageExchangeRates, fetchCurrencyExchangeRate, fetchCurrencyExchangeRates} from './utils';

/**
 * This function will return the stored exchange rate (in local storage) if the date is the same as today.
 * If the exchange is not stored or it's from another day, it will return null
 * @param {String}  baseCurrency
 * @param {String}  targetCurrency
 * @returns {number|null} the exchange rate if available and valid, null otherwise
 */
export const getStoredExchangeRate = (baseCurrency, targetCurrency) => {
  const exchangeRateKey = `${baseCurrency}_${targetCurrency}`;
  const storedExchangeRates = getLocalStorageExchangeRates();

  const selectedExchangeRate = storedExchangeRates[exchangeRateKey];
  const today = new Date().toLocaleDateString();
  // Check if outdated or not present
  if (
    !selectedExchangeRate ||
    storedExchangeRates[exchangeRateKey].error ||
    storedExchangeRates[exchangeRateKey].date !== today
  ) {
    return null;
  }

  return selectedExchangeRate;
};

/**
 * Will try to get the exchange rate from local storage. If it's not available or it's outdated, it will make
 * and API call to get the updated exchange rate. It will also store it in the local storage for future requests
 * @param {String} from transactional currency
 * @param {String} to destination currency
 * @returns {Promise<number>} a promise that will resolve to the updated exchange rate
 */
export const retrieveCurrencyExchangeRate = async (from, to) => {
  if (!from || !to) {
    return null;
  }

  const storedExchangeRate = getStoredExchangeRate(from, to);
  if (!storedExchangeRate) {
    try {
      const exchangeRateResponse = await fetchCurrencyExchangeRate(from, to);

      return exchangeRateResponse;
    } catch (error) {
      return null;
    }
  }

  return storedExchangeRate;
};

/**
 * Will try to get the exchange rates from local storage. If they are not available or are outdated, it will make
 * and API call to get the all the updated exchange rates. It will also store them in the local storage for future requests
 * @param {Array<String>} from A list of currencies to get the exchange rate
 * @param {String} to destination currency
 * @returns {Promise<number>} a promise that will resolve to the updated exchange rate
 */
export const retrieveCurrencyExchangeRates = async (from, to) => {
  if (!from?.length || !to) {
    return Promise.resolve([]);
  }

  const storedExchangeRates = from
    .map((currency) => getStoredExchangeRate(currency, to))
    .filter((exchange) => exchange !== null);
  if (storedExchangeRates.length !== from.length) {
    // We need to get all the missing/outdated exchange rates
    try {
      const exchangeRatesResponse = await fetchCurrencyExchangeRates(from, to);

      return exchangeRatesResponse;
    } catch (error) {
      // At least we can return the stored exchange rates
      return storedExchangeRates;
    }
  }

  return storedExchangeRates;
};

/**
 * Returns the stored value in local storage for the selected currency code. If the value is not set, it will return null
 * @returns the selected currency code or null if empty
 */
export const getSelectedCurrencyCode = () => {
  const currencyCode = localStorage().getItem('selectedCurrencyCode');
  if (['false', 'undefined', 'null', ''].includes(currencyCode)) {
    return null;
  }

  return currencyCode;
};

export {getLocalStorageExchangeRates};
